// Reads los_version from cookie; handles ?los_version= query param on mount.
// Returns the active variant URL override (null means use stable/default).

import { useEffect, useState } from 'react'

import config from 'config'
import {
    getSessionKey,
    removeSessionKey,
    setSessionKey,
} from 'storage/sessionKey'

const COOKIE_NAME = 'los_version'
const COOKIE_TTL_MINUTES = 30
// Only pr-{digits} is a valid variant version — rejects arbitrary injected values.
const VALID_VERSION_RE = /^pr-\d+$/

function getCookie(name: string): string | null {
    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`))

    return match ? decodeURIComponent(match.split('=')[1]) : null
}

function setCookie(name: string, value: string, minutes: number): void {
    const expires = new Date(Date.now() + minutes * 60 * 1000).toUTCString()

    document.cookie = `${name}=${encodeURIComponent(
        value,
    )}; expires=${expires}; path=/; SameSite=Lax; Secure`
}

function deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
}

export function getLosVariantBaseUrl(): string | null {
    // Belt-and-suspenders: never return a variant URL outside sandbox,
    // even if a cookie somehow persists across environments.
    if (config.ENVIRONMENT !== 'sandbox') return null

    const version = getCookie(COOKIE_NAME)

    if (!version || version === 'stable') return null

    // Reject values that don't match pr-{digits} to prevent unintended host routing.
    if (!VALID_VERSION_RE.test(version)) return null

    return `https://los-${version}.sandbox-eks.octane.co`
}

/**
 * Fetch the current session key from the main LOS and persist it to
 * localStorage so that cross-subdomain requests to variant LOS instances
 * can include it as `Authorization: Session <key>`.
 *
 * Background: session cookies are scoped to the exact hostname
 * (SESSION_COOKIE_DOMAIN=None), so the browser will not send the main
 * sandbox sessionid cookie to los-pr-{N}.sandbox-eks.octane.co.  Both
 * environments share the same PostgreSQL session table, so passing the
 * raw key in the Authorization header (picked up by
 * SessionAuthorizationHeaderMiddleware) lets the variant authenticate the
 * user without a full OIDC round-trip.
 *
 * Non-fatal: if the fetch fails the variant requests will receive 401s
 * and the user can manually navigate to the variant URL to re-authenticate.
 */
export async function fetchAndStoreVariantSessionKey(): Promise<void> {
    try {
        // The session key endpoint requires authentication. Dealer-web-app users
        // authenticate via Authorization: Session header (not cookies), and
        // SameSite=Lax cookies won't be sent in cross-subdomain fetch requests
        // anyway. Include the existing key if we have one so the stable LOS can
        // identify the caller. On first login the key was returned by the login
        // endpoint itself, so it's always available by the time this is called.
        const existingKey = getSessionKey()

        const headers: HeadersInit = existingKey
            ? { Authorization: `Session ${existingKey}` }
            : {}

        const resp = await fetch(
            `${config.API_BASE_URL}/api/v3/auth/sessions/key/`,
            { credentials: 'include', headers },
        )

        if (resp.ok) {
            const data = (await resp.json()) as { session_key?: string }

            if (data.session_key) {
                setSessionKey(data.session_key)
            } else {
                // OK response but no key in body — clear any stale key so
                // variant requests don't silently send invalid credentials.
                removeSessionKey()
            }
        } else {
            // Non-OK response (e.g. 401/500) — clear stale key to prevent
            // repeated 401s on variant requests.
            removeSessionKey()
        }
    } catch (err) {
        // Non-fatal — variant requests will 401 if the session key is unavailable.
        // eslint-disable-next-line no-console
        console.warn(
            '[useLosVersion] Failed to fetch variant session key:',
            err,
        )
    }
}

export function useLosVersion() {
    const [losVersion, setLosVersionState] = useState<string>(
        () => getCookie(COOKIE_NAME) ?? 'stable',
    )

    useEffect(() => {
        // Query-param deep linking is sandbox-only; ignore in all other environments.
        if (config.ENVIRONMENT !== 'sandbox') return

        const params = new URLSearchParams(window.location.search)
        const paramVersion = params.get('los_version')
        // If a variant cookie is already set on page load (e.g. after a
        // refresh), refresh the session key so it stays valid.  The key
        // persists in localStorage between page loads but may be stale
        // if the user has been idle for a session lifetime.
        // Skip when a ?los_version= param is also present — the param
        // handler below will fetch the key, avoiding a duplicate request.
        const existingVersion = getCookie(COOKIE_NAME)

        if (
            !paramVersion &&
            existingVersion &&
            VALID_VERSION_RE.test(existingVersion)
        ) {
            void fetchAndStoreVariantSessionKey()
        }

        if (paramVersion === 'stable') {
            // Explicit stable signal from the main sandbox LOS — clear any
            // lingering variant cookie so API calls route to the stable backend.
            deleteCookie(COOKIE_NAME)
            removeSessionKey()
            setLosVersionState('stable')
            params.delete('los_version')

            const newSearch = params.toString()

            window.history.replaceState(
                {},
                '',
                `${window.location.pathname}${newSearch ? '?' + newSearch : ''}${window.location.hash}`,
            )
        } else if (paramVersion && VALID_VERSION_RE.test(paramVersion)) {
            setCookie(COOKIE_NAME, paramVersion, COOKIE_TTL_MINUTES)
            setLosVersionState(paramVersion)
            // Fetch and cache the session key so variant API calls can
            // authenticate via Authorization: Session <key> header.
            void fetchAndStoreVariantSessionKey()
            // Remove param from URL without reload
            params.delete('los_version')
            const newSearch = params.toString()

            const newUrl = `${window.location.pathname}${
                newSearch ? '?' + newSearch : ''
            }${window.location.hash}`

            window.history.replaceState({}, '', newUrl)
        }
    }, [])

    const setLosVersion = async (version: string): Promise<void> => {
        // Cookie writes are sandbox-only; ignore in all other environments.
        if (config.ENVIRONMENT === 'sandbox') {
            if (version === 'stable') {
                deleteCookie(COOKIE_NAME)
                // Clear the stored session key — stable routing uses the
                // native session cookie, not the Authorization header.
                removeSessionKey()
            } else {
                setCookie(COOKIE_NAME, version, COOKIE_TTL_MINUTES)
                // Await the session key fetch before reloading so the key
                // is in localStorage before any variant API requests fire
                // on the fresh page.
                await fetchAndStoreVariantSessionKey()
            }
            // Update state so the UI reflects the new version immediately,
            // then reload to flush all cached component data (SWR caches,
            // local state from global-axios calls, and legacy KO/jQuery
            // calls that bypass the sandboxVariantRoutingInterceptor).
            // Acceptable UX trade-off for a sandbox-only dev tool.

            setLosVersionState(version)

            window.location.reload()

            return
        }

        setLosVersionState(version)
    }

    return { losVersion, setLosVersion }
}
